import api from '@/services/api';

/**
 * Creating a quote request for a generic (non motor/life/health) insurance
 * type.
 *
 * This deliberately does NOT go through `/integrations/quotes/submit/`, the
 * endpoint the motor/life/health branches use: `QuoteSubmitSerializer`
 * resolves `product_type` to exactly one of motor/health/life and then demands
 * the matching `vehicle`/`health`/`life` block, so any other type is rejected
 * before it reaches an adapter. `POST /quotes/requests/` is the line-agnostic
 * way in, and `perform_create` stamps the broker (and POSP) from the caller --
 * the payload never names a broker, so this cannot be pointed at someone
 * else's tenant.
 */

export interface CreatedQuoteRequest {
  id: string;
  request_number?: string;
}

interface QuoteRequestListRow {
  id?: string;
  request_number?: string;
  proposer_phone?: string;
  insurance_type?: string;
  insurance_type_name?: string;
}

/**
 * `QuoteRequestCreateSerializer.Meta.fields` does not list `id`, so a
 * successful create returns the echoed input with no primary key -- and the
 * premium options endpoint is keyed by that id. Read it from the response when
 * it is there (so this heals itself the moment the serializer exposes it), and
 * otherwise fall back to the newest row on the caller's OWN broker-scoped list
 * that matches the proposer we just submitted.
 *
 * The fallback reads through `QuoteRequestViewSet.get_queryset`, so it can only
 * ever surface a request the caller is already allowed to see.
 */
export function extractCreatedId(responseData: unknown): string | null {
  if (!responseData || typeof responseData !== 'object') return null;
  const id = (responseData as { id?: unknown }).id;
  return typeof id === 'string' && id ? id : null;
}

export function pickMatchingRequest(
  rows: QuoteRequestListRow[],
  proposerPhone: string
): string | null {
  const phone = (proposerPhone || '').trim();
  for (const row of rows) {
    if (!row?.id) continue;
    if (!phone || (row.proposer_phone || '').trim() === phone) return row.id;
  }
  return null;
}

export async function createGenericQuoteRequest(
  payload: Record<string, unknown>
): Promise<CreatedQuoteRequest> {
  const response = await api.post('/quotes/requests/', payload);
  const direct = extractCreatedId(response.data);
  if (direct) {
    return { id: direct, request_number: (response.data as { request_number?: string })?.request_number };
  }

  const proposerPhone = String(payload.proposer_phone ?? '');
  const listing = await api.get('/quotes/requests/', {
    params: { ordering: '-created_at', search: proposerPhone || undefined },
  });
  const rows: QuoteRequestListRow[] = listing.data?.results ?? listing.data ?? [];
  const matched = pickMatchingRequest(rows, proposerPhone);
  if (!matched) {
    throw new Error(
      'The quote request was created but its reference could not be read back. Open it from the Quotes list to add premium options.'
    );
  }
  const row = rows.find((r) => r.id === matched);
  return { id: matched, request_number: row?.request_number };
}
